import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { UserPlus, Search, Check, X as XIcon, Clock, Ban, MessageCircle, Copy, Link2 } from 'lucide-react';
import { API, auth, PageHead, Modal, Field, PrimaryBtn, inputCls, errMsg, fmtDate } from '../organization/finance/shared';
import { toast } from '../../lib/toast';

const STATUS_LABELS = { pending: 'معلّقة', accepted: 'مقبولة', rejected: 'مرفوضة', expired: 'منتهية', cancelled: 'ملغاة' };
const STATUS_COLORS = {
  pending: 'bg-amber-50 text-amber-700', accepted: 'bg-teal-50 text-teal-700', rejected: 'bg-red-50 text-red-600',
  expired: 'bg-slate-100 text-slate-500', cancelled: 'bg-slate-100 text-slate-500',
};

const buildWaLink = (phone, url, roleLabel) => {
  const text = encodeURIComponent(`مرحباً، أدعوك للانضمام إلى طاقم عملي في تسيار بصفة "${roleLabel}". سجّل من هنا: ${url}`);
  const digits = (phone || '').replace(/[^\d]/g, '');
  return `https://wa.me/${digits}?text=${text}`;
};

const copyLink = async (url) => {
  try { await navigator.clipboard.writeText(url); toast.success('تم نسخ رابط الدعوة'); }
  catch { toast.error('تعذر نسخ الرابط'); }
};

const InviteModal = ({ open, onClose, roles, onCreated }) => {
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('assistant_coach');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null); // {found, name}
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // {inviteUrl, invitee_phone, roleLabel}

  useEffect(() => {
    if (open) { setPhone(''); setRole('assistant_coach'); setSearchResult(null); setError(null); setResult(null); }
  }, [open]);

  if (!open) return null;

  const doSearch = async () => {
    if (!phone.trim()) { setError('أدخل رقم الجوال أولاً'); return; }
    setSearching(true); setError(null); setSearchResult(null);
    try {
      const res = await axios.get(`${API}/coaches/crew/search`, { ...auth(), params: { phone: phone.trim() } });
      setSearchResult(res.data);
    } catch (e) { setError(errMsg(e, 'تعذر البحث عن هذا الرقم')); }
    setSearching(false);
  };

  const sendInvite = async () => {
    setSending(true); setError(null);
    try {
      const res = await axios.post(`${API}/coaches/crew/invites`, { phone: phone.trim(), role }, auth());
      const roleLabel = roles.find((r) => r.value === role)?.label_ar || role;
      if (res.data.matched) {
        toast.success(`تم إرسال الدعوة إلى ${searchResult?.name || 'المدرب'} داخل المنصة`);
        onCreated();
        onClose();
      } else {
        const inviteUrl = `${window.location.origin}${res.data.invite_path}`;
        setResult({ inviteUrl, invitee_phone: res.data.invite?.invitee_phone, roleLabel });
        onCreated();
      }
    } catch (e) { setError(errMsg(e, 'تعذر إرسال الدعوة')); }
    setSending(false);
  };

  return (
    <Modal open={open} title="دعوة عضو لطاقم العمل" onClose={onClose}>
      {!result ? (
        <div className="space-y-4" data-testid="crew-invite-form">
          <Field label="رقم جوال المدعو" required>
            <div className="flex gap-2">
              <input dir="ltr" value={phone} onChange={(e) => { setPhone(e.target.value); setSearchResult(null); }} placeholder="05xxxxxxxx" className={inputCls} data-testid="crew-invite-phone-input" />
              <button type="button" onClick={doSearch} disabled={searching} className="flex min-h-11 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 disabled:opacity-50" data-testid="crew-invite-search-btn">
                <Search className="h-4 w-4" /> {searching ? 'جارٍ البحث...' : 'بحث'}
              </button>
            </div>
          </Field>
          {searchResult && (
            <div className={`rounded-lg px-3 py-2 text-sm ${searchResult.found ? 'bg-teal-50 text-teal-700' : 'bg-amber-50 text-amber-700'}`} data-testid="crew-invite-search-result">
              {searchResult.found ? `تم العثور على مدرب مسجّل: ${searchResult.name}` : 'لا يوجد حساب مسجّل بهذا الرقم — سيُنشأ رابط دعوة يُرسل عبر واتساب'}
            </div>
          )}
          <Field label="الدور في الطاقم" required>
            <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} data-testid="crew-invite-role-select">
              {roles.map((r) => <option key={r.value} value={r.value}>{r.label_ar}</option>)}
            </select>
          </Field>
          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="crew-invite-error">{error}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600" data-testid="crew-invite-cancel-btn">إلغاء</button>
            <PrimaryBtn onClick={sendInvite} disabled={sending || !searchResult} data-testid="crew-invite-submit-btn">{sending ? 'جارٍ الإرسال...' : 'إرسال الدعوة'}</PrimaryBtn>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="crew-invite-whatsapp-panel">
          <p className="text-sm text-slate-600">تم إنشاء رابط دعوة صالح 7 أيام. لا يملك هذا الرقم حساباً بعد — شارك الرابط، وستظهر له الدعوة تلقائياً للقبول أو الرفض بعد التسجيل.</p>
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
            <span dir="ltr" className="flex-1 truncate text-xs text-slate-600" data-testid="crew-invite-link-text">{result.inviteUrl}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => copyLink(result.inviteUrl)} className="flex min-h-11 items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="crew-invite-copy-link-btn">
              <Copy className="h-4 w-4" /> نسخ الرابط
            </button>
            <a href={buildWaLink(result.invitee_phone, result.inviteUrl, result.roleLabel)} target="_blank" rel="noreferrer" className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#25D366] px-3 text-sm font-bold text-white hover:opacity-90" data-testid="crew-invite-whatsapp-btn">
              <MessageCircle className="h-4 w-4" /> مشاركة واتساب
            </a>
          </div>
          <button type="button" onClick={onClose} className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600" data-testid="crew-invite-done-btn">تم</button>
        </div>
      )}
    </Modal>
  );
};

export default function CoachCrewPage() {
  const [tab, setTab] = useState('sent');
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sentRes, recRes, rolesRes] = await Promise.all([
        axios.get(`${API}/coaches/crew/invites/sent`, auth()),
        axios.get(`${API}/coaches/crew/invites/received`, auth()),
        axios.get(`${API}/coaches/crew/roles`),
      ]);
      setSent(sentRes.data || []);
      setReceived(recRes.data || []);
      setRoles(rolesRes.data || []);
    } catch { /* keep previous state */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancelInvite = async (id) => {
    try { await axios.put(`${API}/coaches/crew/invites/${id}/cancel`, {}, auth()); toast.success('تم إلغاء الدعوة'); load(); }
    catch (e) { toast.error(errMsg(e, 'تعذر إلغاء الدعوة')); }
  };

  const respond = async (id, accept) => {
    try {
      await axios.put(`${API}/coaches/crew/invites/${id}/respond`, { accept }, auth());
      toast.success(accept ? 'تم قبول الدعوة' : 'تم رفض الدعوة');
      load();
    } catch (e) { toast.error(errMsg(e, 'تعذر تنفيذ العملية')); }
  };

  const list = tab === 'sent' ? sent : received;

  return (
    <div className="space-y-6" data-testid="coach-crew-page">
      <PageHead
        title="طاقم العمل"
        desc="ادعُ مساعدين ومختصين للانضمام إلى طاقم عملك الخاص — عبر البحث برقم الجوال أو رابط واتساب"
        action={
          <button onClick={() => setInviteOpen(true)} className="flex min-h-11 items-center gap-2 rounded-lg bg-[#16233b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1e2f4e]" data-testid="crew-add-btn">
            <UserPlus className="h-4 w-4" /> دعوة عضو جديد
          </button>
        }
      />

      <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1" data-testid="crew-tabs">
        <button onClick={() => setTab('sent')} className={`min-h-10 flex-1 rounded-lg text-sm font-semibold transition-colors ${tab === 'sent' ? 'bg-[#16233b] text-white' : 'text-slate-600'}`} data-testid="crew-tab-sent">دعوات أرسلتها ({sent.length})</button>
        <button onClick={() => setTab('received')} className={`min-h-10 flex-1 rounded-lg text-sm font-semibold transition-colors ${tab === 'received' ? 'bg-[#16233b] text-white' : 'text-slate-600'}`} data-testid="crew-tab-received">دعوات وصلتني ({received.length})</button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">جارِ التحميل...</p>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-10 text-center text-sm text-slate-400" data-testid="crew-empty">
          {tab === 'sent' ? 'لم تُرسل أي دعوة بعد' : 'لا توجد دعوات وصلتك حتى الآن'}
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((inv) => (
            <div key={inv.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="crew-invite-row">
              <div>
                <div className="text-sm font-bold text-slate-800">
                  {tab === 'sent' ? (inv.invitee_name || inv.invitee_phone) : inv.inviter_name}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">{inv.role_label} · {fmtDate(inv.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[inv.status]}`} data-testid={`crew-invite-status-${inv.id}`}>{STATUS_LABELS[inv.status]}</span>
                {tab === 'sent' && inv.status === 'pending' && !inv.invitee_name && (
                  <>
                    <button onClick={() => copyLink(`${window.location.origin}${inv.invite_path}`)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" title="نسخ رابط الدعوة" data-testid={`crew-copy-link-btn-${inv.id}`}><Copy className="h-4 w-4" /></button>
                    <a href={buildWaLink(inv.invitee_phone, `${window.location.origin}${inv.invite_path}`, inv.role_label)} target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#25D366]/10 text-[#1a9e52] hover:bg-[#25D366]/20" title="مشاركة واتساب" data-testid={`crew-wa-btn-${inv.id}`}><MessageCircle className="h-4 w-4" /></a>
                  </>
                )}
                {tab === 'sent' && inv.status === 'pending' && (
                  <button onClick={() => cancelInvite(inv.id)} className="flex h-9 w-9 items-center justify-center rounded-lg text-red-500 hover:bg-red-50" title="إلغاء الدعوة" data-testid={`crew-cancel-btn-${inv.id}`}><Ban className="h-4 w-4" /></button>
                )}
                {tab === 'received' && inv.status === 'pending' && (
                  <>
                    <button onClick={() => respond(inv.id, true)} className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100" title="قبول" data-testid={`crew-accept-btn-${inv.id}`}><Check className="h-4 w-4" /></button>
                    <button onClick={() => respond(inv.id, false)} className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100" title="رفض" data-testid={`crew-reject-btn-${inv.id}`}><XIcon className="h-4 w-4" /></button>
                  </>
                )}
                {inv.status === 'pending' && <Clock className="h-3.5 w-3.5 text-slate-300" />}
              </div>
            </div>
          ))}
        </div>
      )}

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} roles={roles} onCreated={load} />
    </div>
  );
}
